import { useEffect, useMemo, useState } from 'react';
import type { DailyStreakSummaryProps, StreakTimelineEntry } from './StreakSummary';

type DebugScenarioDefinition = {
	id: string;
	label: string;
	description: string;
	build: () => Partial<DailyStreakSummaryProps>;
};

type DebugScenarioInstance = DebugScenarioDefinition & {
	props: Partial<DailyStreakSummaryProps>;
};

const STORAGE_KEY = 'suigar.debug.daily-streak-scenario';

const debugAction = (message: string) => () => {
	if (typeof window !== 'undefined') {
		console.info(`[DailyStreakDebug] ${message}`);
	}
};

const createTimelineEntry = (offset: number, status: StreakTimelineEntry['status'], explicitLabel?: string): StreakTimelineEntry => {
	const base = new Date();
	base.setHours(0, 0, 0, 0);
	base.setDate(base.getDate() + offset);

	return {
		label: explicitLabel ?? formatOffsetLabel(offset),
		status,
		date: base,
	};
};

const formatOffsetLabel = (offset: number) => {
	if (offset === 0) return 'Today';
	if (offset === -1) return 'Yesterday';
	if (offset < 0) return `${Math.abs(offset)}d ago`;
	return `+${offset}d`;
};

const createTimeline = (entries: Array<{ offset: number; status: StreakTimelineEntry['status']; label?: string }>) =>
	entries.map((entry) => createTimelineEntry(entry.offset, entry.status, entry.label));

const scenarioDefinitions: DebugScenarioDefinition[] = [
	{
		id: 'not-started',
		label: 'Not started',
		description: 'Fresh player with no streak history yet.',
		build: () => ({
			streak: 0,
			progressPct: 0,
			cycleWagerUsd: 0,
			thresholdUsd: 25,
			availableFreezes: 0,
			maxFreezes: 3,
			completedToday: false,
			canRepair: false,
			streakStartDate: null,
			timeline: createTimeline([
				{ offset: -4, status: 'inactive' },
				{ offset: -3, status: 'inactive' },
				{ offset: -2, status: 'inactive' },
				{ offset: -1, status: 'inactive' },
				{ offset: 0, status: 'pending' },
			]),
			onBuyFreeze: undefined,
			onRepair: undefined,
		}),
	},
	{
		id: 'active-no-wager',
		label: 'Active – no wager today',
		description: 'Streak is active but today’s wager has not started.',
		build: () => ({
			streak: 5,
			progressPct: 0,
			cycleWagerUsd: 0,
			thresholdUsd: 100,
			availableFreezes: 1,
			maxFreezes: 3,
			completedToday: false,
			canRepair: false,
			streakStartDate: subtractDays(5),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'pending' },
			]),
			onBuyFreeze: debugAction('Buy freeze (active – no wager)'),
			onRepair: debugAction('Repair (active – no wager)'),
		}),
	},
	{
		id: 'active-wager-in-progress',
		label: 'Active – wagering now',
		description: 'Today’s wager has started but is not complete.',
		build: () => ({
			streak: 12,
			progressPct: 48,
			cycleWagerUsd: 48,
			thresholdUsd: 100,
			availableFreezes: 2,
			maxFreezes: 3,
			completedToday: false,
			canRepair: false,
			streakStartDate: subtractDays(12),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'ongoing' },
			]),
			onBuyFreeze: debugAction('Buy freeze (active – wagering now)'),
			onRepair: debugAction('Repair (active – wagering now)'),
		}),
	},
	{
		id: 'active-wager-complete',
		label: 'Active – today done',
		description: 'Wager met for today. Streak should feel safe.',
		build: () => ({
			streak: 20,
			progressPct: 100,
			cycleWagerUsd: 150,
			thresholdUsd: 150,
			availableFreezes: 3,
			maxFreezes: 5,
			completedToday: true,
			canRepair: false,
			streakStartDate: subtractDays(20),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'completed' },
			]),
			onBuyFreeze: debugAction('Buy freeze (active – today done)'),
			onRepair: debugAction('Repair (active – today done)'),
		}),
	},
	{
		id: 'needs-repair',
		label: 'Broken – needs repair',
		description: 'User missed a day. Repair or freeze is required.',
		build: () => ({
			streak: 18,
			progressPct: 0,
			cycleWagerUsd: 0,
			thresholdUsd: 120,
			availableFreezes: 0,
			maxFreezes: 3,
			completedToday: false,
			canRepair: true,
			streakStartDate: subtractDays(19),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'missed' },
				{ offset: 0, status: 'missed' },
			]),
			onBuyFreeze: debugAction('Buy freeze (broken – needs repair)'),
			onRepair: debugAction('Repair (broken – needs repair)'),
		}),
	},
	{
		id: 'repaired-today',
		label: 'Repaired today',
		description: 'Freeze applied to cover a miss; streak continues but needs action.',
		build: () => ({
			streak: 18,
			progressPct: 0,
			cycleWagerUsd: 0,
			thresholdUsd: 120,
			availableFreezes: 0,
			maxFreezes: 3,
			completedToday: false,
			canRepair: false,
			streakStartDate: subtractDays(18),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'missed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'pending' },
			]),
			onBuyFreeze: debugAction('Buy freeze (repaired today)'),
			onRepair: debugAction('Repair (repaired today)'),
		}),
	},
	{
		id: 'freeze-active',
		label: 'Freeze ready',
		description: 'User owns freezes and has not wagered yet today.',
		build: () => ({
			streak: 7,
			progressPct: 0,
			cycleWagerUsd: 0,
			thresholdUsd: 80,
			availableFreezes: 2,
			maxFreezes: 3,
			completedToday: false,
			canRepair: false,
			streakStartDate: subtractDays(7),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'pending' },
			]),
			onBuyFreeze: debugAction('Buy freeze (freeze ready)'),
			onRepair: debugAction('Repair (freeze ready)'),
		}),
	},
	{
		id: 'freezes-maxed',
		label: 'Freeze inventory full',
		description: 'All freeze slots are filled to showcase the helper text state.',
		build: () => ({
			streak: 22,
			progressPct: 30,
			cycleWagerUsd: 30,
			thresholdUsd: 100,
			availableFreezes: 5,
			maxFreezes: 5,
			completedToday: false,
			canRepair: false,
			streakStartDate: subtractDays(22),
			timeline: createTimeline([
				{ offset: -4, status: 'completed' },
				{ offset: -3, status: 'completed' },
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'ongoing' },
			]),
			onBuyFreeze: debugAction('Buy freeze (inventory full)'),
			onRepair: debugAction('Repair (inventory full)'),
		}),
	},
	{
		id: 'popover-warning',
		label: 'Popover warning',
		description: 'Small-popover layout with today still pending.',
		build: () => ({
			variant: 'popover',
			streak: 3,
			progressPct: 0,
			cycleWagerUsd: 0,
			thresholdUsd: 60,
			availableFreezes: 1,
			maxFreezes: 3,
			completedToday: false,
			canRepair: false,
			streakStartDate: subtractDays(3),
			timeline: createTimeline([
				{ offset: -2, status: 'completed' },
				{ offset: -1, status: 'completed' },
				{ offset: 0, status: 'pending' },
			]),
			onBuyFreeze: debugAction('Buy freeze (popover warning)'),
			onRepair: debugAction('Repair (popover warning)'),
		}),
	},
];

const subtractDays = (days: number) => {
	const base = new Date();
	base.setHours(0, 0, 0, 0);
	base.setDate(base.getDate() - days);
	return base;
};

const scenarioInstances: DebugScenarioInstance[] = scenarioDefinitions.map((definition) => ({
	...definition,
	props: definition.build(),
}));

function useDailyStreakDebugDev(baseProps: DailyStreakSummaryProps) {
	const hasWindow = typeof window !== 'undefined';

	const [activeScenarioId, setActiveScenarioId] = useState<string>(() => {
		if (!hasWindow) return 'off';
		return window.localStorage.getItem(STORAGE_KEY) ?? 'off';
	});

	useEffect(() => {
		if (!hasWindow) return;
		window.localStorage.setItem(STORAGE_KEY, activeScenarioId);
	}, [activeScenarioId, hasWindow]);

	const activeScenario = useMemo(() => scenarioInstances.find((scenario) => scenario.id === activeScenarioId), [activeScenarioId]);

	const resolvedProps = useMemo<DailyStreakSummaryProps>(() => {
		if (!activeScenario) {
			return baseProps;
		}

		const override = activeScenario.props;

		return {
			...baseProps,
			...override,
			className: override.className ?? baseProps.className,
			variant: override.variant ?? baseProps.variant,
		};
	}, [activeScenario, baseProps]);

	return {
		isAvailable: true,
		scenarios: scenarioInstances,
		activeScenarioId,
		activeScenario,
		setScenario: setActiveScenarioId,
		clearScenario: () => setActiveScenarioId('off'),
		resolvedProps,
	};
}

function useDailyStreakDebugProd(_baseProps: DailyStreakSummaryProps) {
	return null;
}

export const useDailyStreakDebug = import.meta.env.DEV ? useDailyStreakDebugDev : useDailyStreakDebugProd;
